import { maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import type { AvailabilityRequestApi } from 'v5/api/availability-request/availability-request.api';
import {
  appScopeUtils,
  type AppScopeIdentity
} from 'v5/core/app-scope/appScope.types';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { AvailabilityRequestService } from './AvailabilityRequestService';

function scopeIds<T extends { scope: AppScopeIdentity }>({
  scope,
  ...rest
}: T) {
  return {
    ...rest,
    enterpriseWorkspaceId: appScopeUtils.getWorkspaceId(scope),
    enterpriseTeamId: scope.level === 'team' ? scope.teamId : null
  };
}

export function createAvailabilityRequestService(
  config: WithServices<[WithErrorService]> & {
    api: AvailabilityRequestApi;
    queryClient: QueryClient;
  }
): AvailabilityRequestService {
  const {
    api,
    queryClient,
    services: { errorService }
  } = config;
  const interceptor = errorService.withCategory('AvailabilityRequestService');

  /**
   * Sending a request changes what the list holds. The app's QueryClient sets
   * `staleTime: Infinity`, so without this the dashboard's pending count stands
   * still for the rest of the session and only a reload corrects it.
   */
  const invalidateAll = () => {
    queryClient.removeQueries({
      queryKey: ['availability-request'],
      type: 'inactive'
    });
    return queryClient.invalidateQueries({
      queryKey: ['availability-request'],
      type: 'active'
    });
  };

  return {
    createAndSendEmail: interceptor.wrap(async input => {
      const created = await api.createAndSendEmail(scopeIds(input));
      await invalidateAll();
      return created;
    }),
    createLink: interceptor.wrap(async input => {
      const created = await api.createLink(scopeIds(input));
      await invalidateAll();
      return created;
    }),
    useAvailabilityRequests: input =>
      ensureIdleQuery(
        input,
        useQuery(
          {
            enabled: maybe.isPresent(input),
            queryKey: ['availability-request', 'list', input],
            queryFn: interceptor.wrap(({ signal }) =>
              maybe.map(input, value => api.list(value, signal))
            )
          },
          queryClient
        )
      )
  };
}
